import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import ProsMarquee from "../pages/blog/ProsMarquee";
import { weeklyCounter, monthlyCounter } from "../lib/counters";
import { markMiniQuizComplete } from "../lib/miniQuizProgress";

// Mapping ?from=quiz-hub-* → URL cible + label du bouton retour
const HUB_ORIGINS = {
  "quiz-hub-arts": { href: "/mini-quiz-arts-divinatoires", label: "Retour à la liste du mini-quiz" },
  "quiz-hub-energ": { href: "/mini-quiz-pratiques-energetiques", label: "Retour à la liste du mini-quiz" },
  "quiz-hub-paris-arts": { href: "/mini-quiz-paris-arts-divinatoires", label: "Retour aux 20 mini-quiz Paris" },
  "quiz-hub-paris-energ": { href: "/mini-quiz-paris-pratiques-energetiques", label: "Retour aux 20 mini-quiz Paris" },
};

/**
 * Mini-quiz contextuel réutilisable, à placer en bas d'un article SEO.
 *
 * Props :
 *  - theme       : "yellow" (arts div) | "green" (pratiques énergétiques)
 *  - badge       : court label en haut (ex: "✦ Mini-Quiz Express")
 *  - title       : titre principal du quiz (ex: "Voyant ou médium ?")
 *  - subtitle    : sous-titre court
 *  - questions   : [{ text, options: [{ txt, icon, type }] }]
 *  - results     : { [type]: { title, quote, emoji, link, cta } }
 *  - testIdRoot  : préfixe pour les data-testid (unique par quiz)
 */
const PALETTES = {
  yellow: {
    bg1: "#1A1208", bg2: "#2C1F0E",
    main: "#E8A838", dark: "#C47A15", light: "#F5D78E",
    cardBorder: "rgba(232,168,56,0.3)", glow: "rgba(232,168,56,0.08)",
    accent1: "rgba(232,168,56,0.18)", accent2: "rgba(232,168,56,0.05)",
    barShadow: "rgba(232,168,56,0.4)",
    optBg: "rgba(232,168,56,0.05)", optBorder: "rgba(232,168,56,0.18)",
    optHoverBg: "rgba(232,168,56,0.14)", optHoverBorder: "rgba(232,168,56,0.5)",
    ctaText: "#1A1208", ctaShadow: "rgba(232,168,56,0.35)",
    secondaryBorder: "rgba(232,168,56,0.25)",
  },
  green: {
    bg1: "#0F1F14", bg2: "#1A2E1F",
    main: "#2E8B57", dark: "#1A5C38", light: "#A8E6A3",
    cardBorder: "rgba(46,139,87,0.35)", glow: "rgba(46,139,87,0.08)",
    accent1: "rgba(46,139,87,0.18)", accent2: "rgba(46,139,87,0.05)",
    barShadow: "rgba(46,139,87,0.4)",
    optBg: "rgba(46,139,87,0.06)", optBorder: "rgba(46,139,87,0.18)",
    optHoverBg: "rgba(46,139,87,0.14)", optHoverBorder: "rgba(46,139,87,0.5)",
    ctaText: "#0F1F14", ctaShadow: "rgba(46,139,87,0.35)",
    secondaryBorder: "rgba(46,139,87,0.25)",
  },
};

export default function MiniQuizContextuel({
  theme = "yellow",
  badge = "✦ Mini-Quiz Express",
  title,
  subtitle,
  questions,
  results,
  testIdRoot = "mini-quiz",
}) {
  const p = PALETTES[theme] || PALETTES.yellow;
  const [step, setStep]     = useState(0);
  const [counts, setCounts] = useState({});
  const [winner, setWinner] = useState(null);

  const pick = (type) => {
    const next = { ...counts, [type]: (counts[type] || 0) + 1 };
    setCounts(next);
    if (step < questions.length - 1) {
      setStep((s) => s + 1);
    } else {
      let w = null, max = -1;
      for (const k of Object.keys(next)) {
        if (next[k] > max) { max = next[k]; w = k; }
      }
      setWinner(w || Object.keys(results)[0]);
    }
  };

  const restart = () => { setStep(0); setCounts({}); setWinner(null); };

  // Track completion in localStorage dès qu'un résultat est affiché
  useEffect(() => {
    if (winner) markMiniQuizComplete(testIdRoot);
  }, [winner, testIdRoot]);

  const r = winner ? results[winner] : null;
  // Compteurs preuve sociale — hebdo (passages) + mensuel (résultats partagés)
  const weekTesters = weeklyCounter(`${testIdRoot}-week`, 280, 720);
  const monthShares = monthlyCounter(`${testIdRoot}-shared-month`, 80, 280);
  // Catégorie pros à afficher en fin de quiz : vert = energ, jaune = div
  const prosCat = theme === 'green' ? 'energ' : 'div';

  // Détecte si le visiteur vient d'un hub mini-quiz (via ?from=quiz-hub-*)
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromHub = HUB_ORIGINS[searchParams.get("from")] || null;

  return (
    <section className={`mqc-wrap mqc-${theme}`} data-testid={testIdRoot} id="mini-quiz">
      {fromHub && (
        <Link to={fromHub.href} className={`mqc-back-fab mqc-back-fab--${theme}`} data-testid={`${testIdRoot}-back-to-hub`}>
          <span className="mqc-back-fab-icon" aria-hidden="true">←</span>
          <span className="mqc-back-fab-text">{fromHub.label}</span>
        </Link>
      )}
      <div className="mqc-card">
        <div className="mqc-header">
          <span className="mqc-badge">{badge}</span>
          <div className="mqc-social-row" data-testid={`${testIdRoot}-social-counters`}>
            <span className="mqc-social-chip">
              <span className="mqc-social-pulse" />
              <strong>{weekTesters.toLocaleString('fr-FR')}</strong> tests cette semaine
            </span>
            <span className="mqc-social-chip">
              <span className="mqc-social-share">✦</span>
              <strong>{monthShares.toLocaleString('fr-FR')}</strong> partages ce mois-ci
            </span>
          </div>
          <h2 className="mqc-title">{title}</h2>
          {subtitle && <p className="mqc-subtitle">{subtitle}</p>}
        </div>

        {!winner && (
          <div className="mqc-question" data-testid={`${testIdRoot}-step-${step}`}>
            <div className="mqc-progress">
              <div className="mqc-progress-bar" style={{ width: `${((step + 1) / questions.length) * 100}%` }} />
            </div>
            <div className="mqc-step-label">Question {step + 1} sur {questions.length}</div>
            <h3 className="mqc-q-text">{questions[step].text}</h3>
            <div className="mqc-opts">
              {questions[step].options.map((o, i) => (
                <button
                  key={i}
                  className="mqc-opt"
                  onClick={() => pick(o.type)}
                  data-testid={`${testIdRoot}-opt-${step}-${i}`}
                >
                  <span className="mqc-opt-icon">{o.icon}</span>
                  <span className="mqc-opt-txt">{o.txt}</span>
                  <span className="mqc-opt-arrow">→</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {r && (
          <div className="mqc-result" data-testid={`${testIdRoot}-result`}>
            <div className="mqc-result-emoji">{r.emoji}</div>
            <div className="mqc-result-label">✦ Ton orientation idéale ✦</div>
            <h3 className="mqc-result-title">{r.title}</h3>
            <p className="mqc-result-quote">&laquo;&nbsp;{r.quote}&nbsp;&raquo;</p>
            <div className="mqc-result-ctas">
              <Link to={r.link} className="mqc-cta-primary" data-testid={`${testIdRoot}-cta-primary`}>
                {r.cta}
              </Link>
              <button onClick={restart} className="mqc-cta-secondary" data-testid={`${testIdRoot}-cta-restart`}>
                🔄 Refaire le quiz
              </button>
            </div>
          </div>
        )}
      </div>

      {r && (
        <div className="mqc-pros-block" data-testid={`${testIdRoot}-pros-block`}>
          <h4 className="mqc-pros-title">
            {theme === 'green' ? '☯ Nos praticiens énergétiques certifiés' : '💛 Nos voyants & médiums certifiés'}
          </h4>
          <p className="mqc-pros-sub">Sélection de professionnels Premium — clique sur un nom pour voir sa fiche.</p>
          <div className="mqc-pros-wrap">
            <ProsMarquee
              cat={prosCat}
              franceOnly
              titleSuffixTricolore
            />
          </div>
          <Link to={`/annuaire?cat=${prosCat}`} className="mqc-cta-pros" data-testid={`${testIdRoot}-cta-annuaire`}>
            {theme === 'green' ? '⚡ Voir tout l\'annuaire énergétique' : '💛 Voir tout l\'annuaire divinatoire'}
          </Link>
        </div>
      )}

      <style>{`
        .mqc-wrap { padding: 40px 20px 70px; font-family: 'Inter', sans-serif; }

        /* Bouton "Retour à la liste du mini-quiz" — FAB flottant en bas à droite si ?from=quiz-hub-* */
        .mqc-back-fab {
          position: fixed;
          right: 24px; bottom: 24px;
          z-index: 1000;
          display: inline-flex; align-items: center; gap: .55rem;
          padding: .85rem 1.35rem;
          border-radius: 50px; text-decoration: none;
          font-family: 'Inter', sans-serif; font-size: .9rem; font-weight: 700;
          box-shadow: 0 12px 34px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(255,255,255,0.35);
          transition: transform .25s ease, box-shadow .25s ease, filter .25s ease;
        }
        .mqc-back-fab--yellow {
          background: linear-gradient(135deg, #fbbf24 0%, #facc15 50%, #f59e0b 100%);
          color: #1a0a2e;
        }
        .mqc-back-fab--green {
          background: linear-gradient(135deg, #22c55e 0%, #10b981 50%, #059669 100%);
          color: #062929;
        }
        .mqc-back-fab:hover { transform: translateY(-3px); filter: brightness(1.05); box-shadow: 0 16px 42px rgba(0,0,0,0.5); }
        .mqc-back-fab-icon { font-size: 1rem; line-height: 1; }
        @media (max-width: 480px) {
          .mqc-back-fab { right: 12px; bottom: 12px; padding: .7rem 1rem; font-size: .8rem; }
          .mqc-back-fab-text { display: none; }
          .mqc-back-fab-icon { font-size: 1.15rem; }
        }
        .mqc-yellow { background: linear-gradient(180deg, ${p.bg1} 0%, ${p.bg2} 100%); }
        .mqc-green  { background: linear-gradient(180deg, ${p.bg1} 0%, ${p.bg2} 100%); }
        .mqc-card {
          max-width: 720px; margin: 0 auto;
          background: linear-gradient(145deg, rgba(0,0,0,0.45), rgba(0,0,0,0.6));
          border: 1px solid ${p.cardBorder};
          border-radius: 22px;
          padding: 38px 32px;
          position: relative; overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5), 0 0 50px ${p.glow};
        }
        .mqc-card::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
          background: linear-gradient(90deg, ${p.dark}, ${p.main}, ${p.light}, ${p.dark});
          background-size: 200% 100%;
          animation: mqcShimmer 5s linear infinite;
          will-change: background-position;
          contain: layout paint;
        }
        @keyframes mqcShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        .mqc-header { text-align: center; margin-bottom: 28px; }
        .mqc-badge {
          display: inline-block;
          font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700;
          letter-spacing: 4px; text-transform: uppercase;
          color: ${p.light};
          padding: 6px 18px;
          background: linear-gradient(135deg, ${p.accent1}, ${p.accent2});
          border: 1px solid ${p.cardBorder};
          border-radius: 50px;
          margin-bottom: 16px;
        }
        .mqc-title {
          font-family: 'Cinzel', serif;
          font-size: clamp(20px, 3.4vw, 28px);
          font-weight: 800;
          background: linear-gradient(135deg, ${p.light} 0%, ${p.main} 60%, ${p.dark} 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
          margin-bottom: 8px; line-height: 1.25;
        }
        .mqc-subtitle { color: #ffffff; opacity: 0.78; font-size: 14px; }

        .mqc-progress { height: 4px; background: rgba(255,255,255,0.08); border-radius: 10px; overflow: hidden; margin-bottom: 10px; }
        .mqc-progress-bar {
          height: 100%;
          background: linear-gradient(90deg, ${p.dark}, ${p.main}, ${p.light});
          border-radius: 10px;
          transition: width 0.5s cubic-bezier(0.4,0,0.2,1);
          box-shadow: 0 0 8px ${p.barShadow};
        }
        .mqc-step-label {
          font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 3px;
          color: #ffffff; opacity: 0.7; text-transform: uppercase;
          text-align: center; margin-bottom: 18px;
        }
        .mqc-q-text {
          font-family: 'Playfair Display', serif;
          font-size: 19px; font-weight: 600; color: #ffffff;
          text-align: center; line-height: 1.45;
          margin-bottom: 22px;
        }
        .mqc-opts { display: flex; flex-direction: column; gap: 11px; }
        .mqc-opt {
          display: flex; align-items: center; gap: 14px;
          padding: 14px 18px;
          background: ${p.optBg};
          border: 1.5px solid ${p.optBorder};
          border-radius: 14px;
          color: #ffffff; font-size: 14.5px; font-weight: 500;
          cursor: pointer; transition: all 0.3s ease;
          text-align: left; width: 100%;
          font-family: 'Inter', sans-serif;
        }
        .mqc-opt:hover {
          background: ${p.optHoverBg};
          border-color: ${p.optHoverBorder};
          transform: translateX(5px);
        }
        .mqc-opt-icon { font-size: 22px; flex-shrink: 0; }
        .mqc-opt-txt { flex: 1; color: #ffffff; }
        .mqc-opt-arrow { color: ${p.light}; opacity: 0.6; transition: transform 0.3s ease; }
        .mqc-opt:hover .mqc-opt-arrow { transform: translateX(4px); opacity: 1; }

        .mqc-result { text-align: center; animation: mqcResultIn 0.7s ease forwards; }
        @keyframes mqcResultIn { from { opacity:0; transform: translateY(20px); } to { opacity:1; transform: translateY(0); } }
        .mqc-result-emoji {
          font-size: 64px; margin-bottom: 12px;
          filter: drop-shadow(0 0 25px ${p.barShadow});
          animation: mqcFloat 3s ease-in-out infinite;
        }
        @keyframes mqcFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        .mqc-result-label {
          font-family: 'Cinzel', serif; font-size: 11px;
          letter-spacing: 4px; text-transform: uppercase;
          color: ${p.light}; margin-bottom: 10px;
        }
        .mqc-result-title {
          font-family: 'Cinzel', serif; font-size: clamp(22px, 4vw, 28px); font-weight: 800;
          color: #ffffff; letter-spacing: 1.5px; margin-bottom: 14px;
        }
        .mqc-result-quote {
          font-family: 'Playfair Display', serif; font-style: italic;
          font-size: 15.5px; color: ${p.light};
          max-width: 480px; margin: 0 auto 26px; line-height: 1.65;
        }
        .mqc-result-ctas { display: flex; flex-direction: column; gap: 12px; max-width: 380px; margin: 0 auto; }
        .mqc-cta-primary {
          display: inline-flex; align-items: center; justify-content: center;
          padding: 15px 28px; border-radius: 50px;
          background: linear-gradient(135deg, ${p.main}, ${p.dark});
          color: ${p.ctaText}; text-decoration: none;
          font-family: 'Cinzel', serif; font-size: 13px; font-weight: 900;
          letter-spacing: 2px; text-transform: uppercase;
          transition: all 0.3s ease;
          box-shadow: 0 8px 25px ${p.ctaShadow};
        }
        .mqc-cta-primary:hover { transform: translateY(-3px); box-shadow: 0 12px 35px ${p.ctaShadow}; }
        .mqc-cta-secondary {
          padding: 12px 24px; border-radius: 50px;
          background: transparent;
          border: 1px solid ${p.secondaryBorder};
          color: ${p.light};
          font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
          cursor: pointer; transition: all 0.3s ease;
        }
        .mqc-cta-secondary:hover { border-color: ${p.main}; color: #ffffff; }

        @media (max-width: 560px) {
          .mqc-card { padding: 28px 22px; }
          .mqc-q-text { font-size: 17px; }
          .mqc-opt { padding: 13px 15px; font-size: 13.5px; }
          .mqc-social-row { gap: .45rem; flex-direction: column; align-items: center; }
        }

        /* === Preuve sociale : compteurs hebdo + mensuels === */
        .mqc-social-row {
          display: inline-flex; gap: .65rem; flex-wrap: wrap; justify-content: center;
          margin: 0 0 16px;
        }
        .mqc-social-chip {
          display: inline-flex; align-items: center; gap: .4rem;
          background: rgba(0,0,0,0.3);
          border: 1px solid ${p.secondaryBorder};
          padding: .35rem .75rem; border-radius: 50px;
          font-family: 'Inter', sans-serif; font-size: .78rem;
          color: rgba(255,255,255,0.88);
        }
        .mqc-social-chip strong { color: ${p.light}; font-weight: 700; }
        .mqc-social-pulse {
          width: 7px; height: 7px; border-radius: 50%; background: ${p.main};
          box-shadow: 0 0 0 0 ${p.barShadow};
          animation: mqcPulse 2s infinite;
        }
        .mqc-social-share { color: ${p.light}; font-size: .88rem; }
        @keyframes mqcPulse {
          0% { box-shadow: 0 0 0 0 ${p.barShadow}; }
          70% { box-shadow: 0 0 0 10px transparent; }
          100% { box-shadow: 0 0 0 0 transparent; }
        }

        /* === Bloc pros affiché APRÈS résultat (CTA + marquee bichromique) === */
        .mqc-pros-block {
          max-width: 1100px; margin: 1.6rem auto 0;
          padding: 1.4rem 1rem 1rem;
          background: #000000;
          border: 1px solid ${p.cardBorder};
          border-radius: 18px;
          text-align: center;
          animation: mqcResultIn 0.7s ease forwards;
        }
        .mqc-pros-title {
          font-family: 'Cinzel', serif; font-size: 1.05rem; font-weight: 700;
          color: ${p.light}; margin-bottom: .35rem; letter-spacing: .5px;
        }
        .mqc-pros-sub {
          font-family: 'Inter', sans-serif; font-size: .85rem;
          color: rgba(255,255,255,0.78); margin-bottom: .8rem;
        }
        .mqc-pros-wrap { margin-bottom: 1rem; }
        .mqc-cta-pros {
          display: inline-flex; align-items: center; gap: .55rem;
          padding: .8rem 1.6rem; border-radius: 50px;
          background: linear-gradient(135deg, ${p.main}, ${p.dark});
          color: ${p.ctaText}; text-decoration: none;
          font-family: 'Cinzel', serif; font-size: .82rem; font-weight: 800;
          letter-spacing: 1.5px; text-transform: uppercase;
          transition: transform .25s ease, box-shadow .25s ease;
          box-shadow: 0 8px 22px ${p.ctaShadow};
        }
        .mqc-cta-pros:hover { transform: translateY(-2px); box-shadow: 0 12px 30px ${p.ctaShadow}; }
      `}</style>
    </section>
  );
}
